import React, { useContext } from 'react'
import styled, { css } from 'styled-components'
import { string, array, func, bool } from 'prop-types'
import { Link } from 'gatsby'
import { rgba } from 'polished'

import * as spacing from 'styles/spacing'
import { greyDark, white, borderColor } from 'styles/colors'
import { animationTime, animationCurve, navHeight, boxShadow } from 'styles/global'
import { fontSizes, fontWeights } from 'styles/typography'
import { media } from 'styles/media'

import Wrapper from 'components/Wrapper'
import NavLabel from './NavLabel'
import NativeAuthContext from 'components/NativeAuth'

const Container = styled.div`
  position: static;
`

const Trigger = styled.button`
  display: flex;
  align-items: center;
  height: ${navHeight};
  margin-right: ${spacing.large};
  transition: color ${animationTime} ${animationCurve};

  ${media.large`
    margin-right: ${spacing.xLarge};
  `};

  ${media.xLarge`
    margin-right: ${spacing.xxLarge};
  `};
`

const Dropdown = styled.div`
  position: absolute;
  top: ${navHeight};
  left: 0;
  width: 100%;
  background-color: ${rgba(white, 0.98)};
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity ${animationTime} ${animationCurve}, visibility ${animationTime} ${animationCurve};
  z-index: 10;
  ${boxShadow};

  ${props =>
    props.hovered &&
    css`
      opacity: 1;
      visibility: visible;
      pointer-events: all;
    `}
`

const Outer = styled.div`
  border-top: 1px solid ${borderColor};
  position: relative;
  width: 100%;
  padding: ${spacing.large} 0;

  ${media.large`
    padding: ${spacing.xLarge} 0;
  `};

  ${media.xLarge`
    padding: ${spacing.xxLarge} 0;
  `};
`

const Inner = styled.div`
  display: flex;
  align-items: flex-start;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`

const CollectionContainer = styled.div`
  display: flex;
  flex: 1;
  color: ${greyDark};
  text-align: left;
  transition: color ${animationTime} ${animationCurve};

  & + & {
    margin-left: ${spacing.large};

    ${media.large`
      margin-left: ${spacing.xxLarge};
    `};
  }
`

const CollectionMain = styled.div`
  display: flex;
  flex-direction: column;
`

const Heading = styled.strong`
  display: block;
  margin-bottom: ${spacing.medium};
`

const List = styled.div`
  flex: 1;
  padding-left: ${spacing.medium};
  border-left: 1px solid ${borderColor};
`

const Item = styled(props => <Link {...props} />)`
  display: block;
  text-align: left;
  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.medium};

  & + & {
    margin-top: ${spacing.small};
  }
`

const Figure = styled.img`
  position: static;
  width: 30px;
  height: 78px;
  align-self: flex-start;
  margin-top: 40px;
  margin-right: ${spacing.medium};
  flex-shrink: 0;
`

const ProductsMenu = ({ hoverFunc, hoverState }) => {
  const { province } = useContext(NativeAuthContext)

  return (
    <Container
      onMouseEnter={() => {
        hoverFunc(true)
      }}
      onMouseLeave={() => {
        hoverFunc(false)
      }}
    >
      <Trigger>
        <NavLabel data-tracking="nav--link--text--Vape_Kits">Products</NavLabel>
      </Trigger>
      <Dropdown hovered={hoverState}>
        <Outer>
          <Wrapper>
            <Inner>
              {province !== 'BC' && (
                <Collection
                  heading="ePen"
                  figure="epen"
                  items={[
                    {
                      label: 'Device',
                      link: '/buy-online/epen-3/',
                      dataTracking: 'nav--link--text--ePen3_Starter_Kit'
                    },
                    {
                      label: 'Pods',
                      link: '/buy-online/vype-epen-3-cartridges/',
                      dataTracking: 'nav--link--text--ePen3_Cartridges'
                    }
                  ]}
                />
              )}

              <Collection
                heading="ePod"
                figure="epod"
                items={[
                  {
                    label: 'Device',
                    link: '/vype-epod-starter-kit/',
                    dataTracking: 'nav--link--text--ePod_Starter_Kit'
                  },
                  {
                    label: 'Pods',
                    link: '/buy-online/vype-epod-vpro-cartridges/',
                    dataTracking: 'nav--link--text--ePod_Pods'
                  },
                  {
                    label: 'Skins',
                    link: '/buy-online/vype-epod-skins/',
                    dataTracking: 'nav--link--text--ePod_Skins'
                  },
                  {
                    label: 'Accessories',
                    link: '/buy-online/vype-epod-accessories/',
                    dataTracking: 'nav--link--text--ePod_Charger'
                  }
                ]}
              />
            </Inner>
          </Wrapper>
        </Outer>
      </Dropdown>
    </Container>
  )
}

ProductsMenu.propTypes = {
  hoverFunc: func.isRequired,
  hoverState: bool
}

const Collection = ({ heading, figure, items }) => (
  <CollectionContainer>
    <Figure src={`${process.env.BASE_PATH}/images/menu/${figure}.svg`} alt={heading} />
    <CollectionMain>
      <Heading>{heading}</Heading>
      <List>
        {items.map((item, i) => (
          <Item to={item.link} key={i} data-tracking={item.dataTracking}>
            {item.label}
          </Item>
        ))}
      </List>
    </CollectionMain>
  </CollectionContainer>
)

Collection.propTypes = {
  heading: string.isRequired,
  figure: string.isRequired,
  items: array
}

export default ProductsMenu
